import { db } from "../firebase";
import { 
  collection, getDocs, getDoc, addDoc, updateDoc, deleteDoc, doc, query, where, writeBatch 
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { getCurrentUserPermission, getEffectiveUserId } from "./userService";

const paymentCollection = collection(db, "payments");

// Vérifie la permission "cash" de l'utilisateur courant
async function assertCashAccess() {
  const perm = await getCurrentUserPermission("cash");
  return perm !== "none";
}

// Récupère les paiements de l'utilisateur effectif
export const fetchPayments = async () => {
  if (!(await assertCashAccess())) return [];
  const userId = await getEffectiveUserId();

  const paymentQuery = query(
    paymentCollection,
    where("userId", "==", userId)
  );
  const querySnapshot = await getDocs(paymentQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) || [];
};

export const fetchPaymentsByProjectId = async (projectId) => {
  if (!(await assertCashAccess())) return [];
  const userId = await getEffectiveUserId();

  const paymentQuery = query(
    paymentCollection,
    where("userId", "==", userId),
    where("projectId", "==", projectId)
  );
  const querySnapshot = await getDocs(paymentQuery);
  return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

export const getPaymentById = async (id) => {
  if (!(await assertCashAccess())) return null;
  const paymentRef = doc(db, "payments", id);
  const paymentDoc = await getDoc(paymentRef);
  if (paymentDoc.exists()) {
    return { id: paymentDoc.id, ...paymentDoc.data() };
  } else {
    throw new Error("Payment not found");
  }
};

export const addPayment = async (payment) => {
  if (!(await assertCashAccess())) return null;
  const userId = await getEffectiveUserId();

  const newPayment = { ...payment, userId };

  const docRef = await addDoc(paymentCollection, newPayment);
  return { id: docRef.id, ...newPayment };
};

export const updatePayment = async (payment) => {
  if (!(await assertCashAccess())) return;
  const paymentRef = doc(db, "payments", payment.id);
  await updateDoc(paymentRef, { ...payment });
};

export const deletePayment = async (paymentId) => {
  if (!(await assertCashAccess())) return;
  const paymentRef = doc(db, "payments", paymentId);
  await deleteDoc(paymentRef);
};

export const addRecurringPayments = async (payment, frequencyStep = 1) => {
  if (!(await assertCashAccess())) return;
  const recurringId = uuidv4();
  const paymentsToAdd = [];
  const startDate = new Date(payment.date);
  const endDate = new Date(payment.dateEnd);
  let currentDate = new Date(startDate);

  while (currentDate <= endDate) {
    paymentsToAdd.push({
      ...payment,
      date: currentDate.toISOString().substr(0, 10),
      recurringId,
    });
    currentDate.setMonth(currentDate.getMonth() + frequencyStep);
  }

  const userId = await getEffectiveUserId();
  const paymentsWithUserId = paymentsToAdd.map(p => ({ ...p, userId }));
  const promises = paymentsWithUserId.map(p => addDoc(paymentCollection, p));
  const docRefs = await Promise.all(promises);

  return docRefs.map((docRef, index) => ({ id: docRef.id, ...paymentsWithUserId[index] }));
};

export const updateRecurringPayments = async (recurringId, updatedFields) => {
  if (!(await assertCashAccess())) return;
  const q = query(paymentCollection, where("recurringId", "==", recurringId));
  const querySnapshot = await getDocs(q);

  const batch = writeBatch(db);
  querySnapshot.forEach(docSnapshot => {
    batch.update(docSnapshot.ref, updatedFields);
  });

  await batch.commit();
};

export const deleteRecurringPayments = async (recurringId) => {
  if (!(await assertCashAccess())) return;
  const q = query(paymentCollection, where("recurringId", "==", recurringId));
  const querySnapshot = await getDocs(q);
  const batch = writeBatch(db);
  querySnapshot.forEach(docSnapshot => {
    batch.delete(docSnapshot.ref);
  });
  await batch.commit();
};